import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { pushToSlice, updateSliceData } from 'infra/redux/sliceHandlers'
import { cn, enqueueNotification, getCurrentOrg } from 'Utils/Helpers'
import { LoadingFeedback, PrefixSuffixInput } from 'V2Components'
import {
  AppNameKey,
  HIDDEN_APPLICATION_FRONTEND_URL_PORTS,
  McpAuthTokenKey,
  McpBackendURLKey,
  McpFrontendURLKey,
  McpHTTPEntryPathKey,
  McpHeaders,
  McpMeshClusterKey,
  createApplication,
  getFrontendNameFromURL,
  updateApplication,
  checkValidHostname,
} from 'features/applications'
import _ from 'lodash'
import { Button, IconButton, Label, SelectDropDown, TextInput, Typography } from 'procyon-ui'
import React, { useEffect, useState } from 'react'
import { useApplicationEditContext } from '../../../providers/ApplicationEditProvider'
import { useUser } from 'Core/Hooks/useUser'
import { useHistory } from 'react-router'
import { faTrash } from '@fortawesome/free-solid-svg-icons'

function MCPApplicationForm({ onCancel, onComplete }) {
  const history = useHistory()
  const { isEditMode, app } = useApplicationEditContext()
  const { rdpurl, isAgentlessMode } = useUser()
  const [isLoading, setIsLoading] = useState(false)
  const [appName, setAppName] = useState(_.get(app, AppNameKey, ''))
  const [beURL, setBeURL] = useState(_.get(app, McpBackendURLKey, '')+_.get(app, McpHTTPEntryPathKey, ''))
  const [fnURL, setFnURL] = useState(getFrontendNameFromURL(_.get(app, McpFrontendURLKey, '')))
  const [entryPath, setEntryPath] = useState(_.get(app, McpHTTPEntryPathKey, '/'))
  const [clusterID, setClusterID] = useState(_.get(app, McpMeshClusterKey, ''))
  const [authToken, setAuthToken] = useState(_.get(app, McpAuthTokenKey, ''))
  const initialMap = _.get(app, McpHeaders, {})
  
  const { slices } = useMultiSlice(['medusaNodes', 'applicationList'])

  const getMeshClusterIds = () => {
    const ids = []
    if (app) ids.push(_.get(app, McpMeshClusterKey))
    ids.push(...slices.medusaNodes.map((node) => node.ClusterID))
    return [...new Set(ids)]
  }

  const getErrors = () => {
    // Not needed to validate app name when in edit mode
    if (!isEditMode && _.find(slices.applicationList, { ObjectMeta: { Name: appName } })) {
      return 'Application name is already taken!'
    }
    if (appName.length <= 3) return 'Application Name must be greater than 3 characters.'
    if (!fnURL) return 'Frontend URL must not be empty.'
    const f = `${fnURL}.${getCurrentOrg()}.applications.procyon.ai`
    if (checkValidHostname(f)) return checkValidHostname(f)
    if (!beURL) return 'MCP Server URL must not be empty.'
    if (!clusterID) return 'Please select the Mesh Cluster.'
  }

  const splitBeFe = (u) => {
    const protoIdx = u.indexOf('://')
    const start = protoIdx >= 0 ? protoIdx + 3 : 0
    const slashIdx = u.indexOf('/', start)

    const base = slashIdx === -1 ? u : u.slice(0, slashIdx)
    const path = slashIdx === -1 ? '/' : u.slice(slashIdx) || '/'

    return { base, path }
  }

  const toRows = (map) => {
    const entries = Object.entries(map || {})
    if (entries.length === 0) return [newRow()]
    return entries.map(([k, v]) => ({ id: makeId(k), k, v: v ?? '' }))
  }

  const toMap = (rows) =>
    rows.reduce((acc, r) => {
      const key = (r.k || '').trim()
      if (key) acc[key] = r.v ?? ''
      return acc
    }, {})

  const makeId = (seed = '') => `${seed}-${Math.random().toString(36).slice(2, 9)}`
  const newRow = () => ({ id: makeId(String(Date.now())), k: '', v: '' })
  const [rows, setRows] = useState(() => toRows(initialMap))
  const [headerMap, setHeaderMap] = useState(() => initialMap)

  useEffect(() => {
    setHeaderMap(toMap(rows))
  }, [rows])

  
  useEffect(() => {
    setRows(toRows(initialMap))
  }, [app]) 

  const updateRow = (id, field, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)))

  const addRow = () => setRows((prev) => [...prev, newRow()])

  const removeRow = (id) =>
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)))

  const generateKeyValueRows = () => (
    <>
      {rows.map((row, index) => (
        <div key={row.id} className='flex gap-4 mt-2 items-center'>
          <TextInput
            placeholder='key'
            value={row.k}
            onChange={(e) => updateRow(row.id, 'k', e.target.value)}
          />
          <TextInput
            placeholder='value'
            value={row.v}
            onChange={(e) => updateRow(row.id, 'v', e.target.value)}
          />
          {rows.length > 1 && (
            <IconButton onClick={() => removeRow(row.id)} variant='grayRed' icon={faTrash} />
          )}

          {index === 0 && (
             <div>
             <Button onClick={addRow} variant='primary'>Add Header +</Button>
           </div>
          )}
        </div>
      ))}

     
    </>
  )

  const handleSubmit = async () => {
    setIsLoading(true)
    const { base, path } = splitBeFe(beURL)
    setBeURL(base)
    setEntryPath(path)

    try {
      let appResp
      if (isEditMode) {
        const updateData = {
          app,
          fnURL: `${fnURL}.${getCurrentOrg()}.applications.procyon.ai`,
          beURL: base,
          clusterID,
          entryPath: path,
          token: authToken,
          headerMap
        }
        appResp = await handleMCPApplicationUpdate(updateData)
        updateSliceData(app)
      } else {
        const createData = {
          appName,
          fnURL: `${fnURL}.${getCurrentOrg()}.applications.procyon.ai`,
          beURL: base,
          clusterID,
          entryPath: path,
          token: authToken,
          headerMap
        }
        appResp = await handleMCPApplicationCreate(createData)
        pushToSlice(app)
      }
      enqueueNotification(
        `Successfully ${isEditMode ? 'Updated' : 'Created'} MCP Server Application!`,
        'info'
      )
      onComplete?.()
      window.location.reload()
      // history.push(`/admin/applications/${encodeURIComponent(appResp.ObjectMeta.Name)}`)
    } catch (error) {
      enqueueNotification(
        `Failed to ${isEditMode ? 'Update' : 'Create'} MCP Server Application!`,
        'error'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const onNameInputChange = (value) => {
    if (value.endsWith('.')) {
      value = value.slice(0, -1)
    }
    setAppName(value)
  }

  const formErrors = getErrors()
  const port = location.port
  // Apply port suffix if the current port is not in the listed ports
  const applyPortSuffix = port && !HIDDEN_APPLICATION_FRONTEND_URL_PORTS.includes(port)
  let suffix = isAgentlessMode
    ? `-${getCurrentOrg()}.applications.${rdpurl}`
    : `.${getCurrentOrg()}.applications.procyon.ai${applyPortSuffix ? `:${port}` : ''}`

  return (
    <div>
      <Typography variant='body-regular'>
        Easily integrate MCP Server applications by providing the following information.
      </Typography>
      <div className='mt-4'>
        <TextInput
          disabled={isEditMode}
          value={appName}
          onChange={(e) => onNameInputChange(e.target.value)}
          sx={{ width: '100%' }}
          label='Application Name'
        />
        <div className='mt-4'>
          <Typography className='mb-1' variant='body-regular'>
            Frontend URL
          </Typography>
          <PrefixSuffixInput
            prefix='https://'
            suffix={suffix}
            value={fnURL}
            onTextChange={setFnURL}
          />
        </div>
        <div className='mt-4'>
          <TextInput
            value={beURL}
            onChange={(e) => setBeURL(e.target.value)}
            sx={{ width: '100%' }}
            label='MCP Server URL'
          />
        </div>
        <div className='mt-4'>
          <TextInput
            value={authToken}
            onChange={(e) => setAuthToken(e.target.value)}
            sx={{ width: '100%' }}
            label='Auth Token'
          />
        </div>
        <div className='mt-4'>
          <Typography variant='body-regular'>Headers</Typography>

          {generateKeyValueRows()}
        </div>
        <div className='mt-4'>
          <Typography className='mb-1' variant='body-regular'>
            Mesh Cluster
          </Typography>
          <SelectDropDown
            showHelpText={false}
            sx={{ width: '100%' }}
            menuItems={getMeshClusterIds().map((e) => ({
              label: e,
              value: e
            }))}
            onChange={(e) => setClusterID(e.target.value)}
            value={clusterID}
          />
        </div>
      </div>
      <div
        className={cn('mt-8 flex items-center gap-8', {
          'justify-between': formErrors,
          'justify-end': !formErrors
        })}
      >
        {formErrors && <Label variant='warning' text={formErrors} />}
        <div className='flex gap-4'>
          <Button onClick={onCancel} variant='gray'>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!!formErrors} variant='primary'>
            {isEditMode ? 'Save Changes' : 'Create'}
          </Button>
        </div>
      </div>
      <LoadingFeedback
        caption='Please wait..'
        loading={isLoading}
        message={`${isEditMode ? 'Updating ' + appName : 'Creating MCP Server Application'}`}
      />
    </div>
  )
}

const handleMCPApplicationCreate = async ({ appName, fnURL, beURL, clusterID, entryPath, token, headerMap }) => {
  const port = location.port
  // Apply port suffix if the current port is not in the listed ports
  const applyPortSuffix = port && !HIDDEN_APPLICATION_FRONTEND_URL_PORTS.includes(port)
  const data = {
    ObjectMeta: {
      Name: appName
    },
    Spec: {
      AppType: 'mcp',
      McpAppConfig: {
        FrontEndName: {
          Elems: [applyPortSuffix ? `${fnURL}:${port}` : fnURL]
        },
        Backends: {
          Elems: [beURL]
        },
        MeshCluster: clusterID,
        EntryPath: entryPath,
        AuthToken: token,
        Headers: {
          Map: headerMap
        }
      }
    }
  }

  return await createApplication(data)
}

const handleMCPApplicationUpdate = async ({ app, fnURL, beURL, clusterID, entryPath, token, headerMap }) => {
  const port = location.port
  // Apply port suffix if the current port is not in the listed ports
  const applyPortSuffix = port && !HIDDEN_APPLICATION_FRONTEND_URL_PORTS.includes(port)

  const data = structuredClone(app)
  // apply port suffix if necessary
  _.set(data, McpFrontendURLKey, applyPortSuffix ? `${fnURL}:${port}` : fnURL)
  _.set(data, McpBackendURLKey, beURL)
  _.set(data, McpMeshClusterKey, clusterID)
  _.set(data, McpHTTPEntryPathKey, entryPath || '/')
  _.set(data, McpAuthTokenKey, token)
  _.set(data, McpHeaders, headerMap)

  return await updateApplication(data)
}

export { MCPApplicationForm }
